/* ╔══════════════════════════════════════════════════════════════╗
   ║  Ultra-Modern Reviews Section — Scripts                     ║
   ║  ~6 KB · ReviewsWall slider · ReviewMediaGallery per card  ║
   ╚══════════════════════════════════════════════════════════════╝ */

/* ────────────────────────────────────────────────────────────────
   ReviewMediaGallery — lazy-loaded media gallery inside a review
   card. Same patterns as ProductGallery: IntersectionObserver,
   muted video autoplay, touch swipe, play-button support.
   ──────────────────────────────────────────────────────────────── */
class ReviewMediaGallery {
  constructor(el) {
    this.el = el;
    this.viewer = el.querySelector('.rv-gallery__viewer');
    this.track = el.querySelector('.rv-gallery__track');
    this.slides = [...el.querySelectorAll('.rv-gallery__slide')];
    this.count = this.slides.length;
    if (this.count < 2) {
      this._initSingle();
      return;
    }

    this.index = 0;
    this.videoPlaying = false;
    this.touch = { startX: 0, startY: 0, deltaX: 0, time: 0 };
    this.dragging = false;

    this.arrows = {
      prev: el.querySelector('.rv-gallery__arrow--prev'),
      next: el.querySelector('.rv-gallery__arrow--next'),
    };
    this.dots = [...el.querySelectorAll('.rv-gallery__dot')];

    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);

    this._init();
  }

  /* ── Single-slide: lazy-load when the gallery nears the viewport ─ */
  _initSingle() {
    // No eager fetch — the reviews section usually sits far below the fold
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this._loadSlide(0);
        obs.disconnect();
      }
    }, { rootMargin: '300px 0px' });
    obs.observe(this.el);
  }

  /* ── Multi-slide init ─────────────────────────────────────── */
  _init() {
    // Load the first slides only when the gallery nears the viewport
    // (rootMargin 300px) — never on page load for a below-the-fold section
    this._obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this._loadSlide(0);
        if (this.count > 1) this._loadSlide(1);
        this._loadAdjacent();
        this._obs.disconnect();
      }
    }, { rootMargin: '300px 0px' });
    this._obs.observe(this.el);

    // Arrows
    if (this.arrows.prev) this.arrows.prev.addEventListener('click', () => this.prev());
    if (this.arrows.next) this.arrows.next.addEventListener('click', () => this.next());

    // Dots
    this.dots.forEach(d => d.addEventListener('click', () => this.goTo(Number(d.dataset.index))));

    // Touch
    this.viewer.addEventListener('touchstart', this._onTouchStart, { passive: false });
    this.viewer.addEventListener('touchmove', this._onTouchMove, { passive: false });
    this.viewer.addEventListener('touchend', this._onTouchEnd);

    // Play button
    this.viewer.addEventListener('click', (e) => {
      if (e.target.closest('.rv-gallery__play')) this._playCurrentVideo();
    });

    // Initial aria state only — no media loads here (they happen when the
    // gallery nears the viewport via the observer above)
    this._updateAriaState(0);
  }

  /* ── Navigation ───────────────────────────────────────────── */
  goTo(idx, { animate = true } = {}) {
    if (idx === this.index || idx < 0 || idx >= this.count) return;
    const prev = this.index;
    this.index = idx;
    if (!animate) this.track.style.transition = 'none';
    this.track.style.transform = `translateX(${-idx * 100}%)`;
    if (!animate) { this.track.offsetHeight; this.track.style.transition = ''; }
    this._updateUI(prev);
  }
  next() { this.goTo(this.index + 1); }
  prev() { this.goTo(this.index - 1); }

  destroy() {
    if (this._obs) this._obs.disconnect();
    if (this.arrows.prev) this.arrows.prev.removeEventListener('click', () => this.prev());
    if (this.arrows.next) this.arrows.next.removeEventListener('click', () => this.next());
    this.dots.forEach(d => d.removeEventListener('click', () => {}));
    this.viewer.removeEventListener('touchstart', this._onTouchStart);
    this.viewer.removeEventListener('touchmove', this._onTouchMove);
    this.viewer.removeEventListener('touchend', this._onTouchEnd);
  }

  /* ── Lazy loading ─────────────────────────────────────────── */
  _loadSlide(idx) {
    const slide = this.slides[idx];
    if (!slide || slide.dataset.rvLoaded === '1') return;
    const media = slide.querySelector('.rv-gallery__media');
    if (!media) return;
    const type = slide.dataset.type;

    if (type === 'image') {
      if (media.dataset.src) media.src = media.dataset.src;
    } else if (type === 'video') {
      if (media.dataset.src && !media.querySelector('source')) {
        const s = document.createElement('source');
        s.src = media.dataset.src;
        s.type = 'video/mp4';
        media.appendChild(s);
      }
      if (media.dataset.poster) media.poster = media.dataset.poster;
    }

    slide.dataset.rvLoaded = '1';
    slide.classList.add('rv-loaded');
  }

  _loadAdjacent() {
    for (let i = Math.max(0, this.index - 1); i <= Math.min(this.count - 1, this.index + 1); i++) {
      this._loadSlide(i);
    }
  }

  _isInViewport() {
    const r = this.el.getBoundingClientRect();
    return r.top < window.innerHeight && r.bottom > 0;
  }

  /* ── UI update ────────────────────────────────────────────── */
  _updateUI(prevIndex) {
    this._loadAdjacent();
    if (prevIndex >= 0 && prevIndex !== this.index) this._pauseSlideVideo(prevIndex);
    if (prevIndex !== this.index) this._autoPlaySlideVideo(this.index);
    this._updateAriaState();
  }

  /* Aria/inert/dots only — no media loading (used at init) */
  _updateAriaState() {
    this.slides.forEach((s, i) => {
      const a = i === this.index;
      s.setAttribute('aria-hidden', a ? 'false' : 'true');
      if (a) s.removeAttribute('inert'); else s.setAttribute('inert', '');
    });

    this.dots.forEach((d, i) => {
      d.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
    });
  }

  /* ── Video playback ───────────────────────────────────────── */
  _pauseSlideVideo(idx) {
    const slide = this.slides[idx];
    if (!slide) return;
    const video = slide.querySelector('video');
    if (video) { video.pause(); video.onended = null; slide.classList.remove('rv-playing'); }
    this.videoPlaying = false;
  }

  _autoPlaySlideVideo(idx) {
    const slide = this.slides[idx];
    if (!slide || slide.dataset.type !== 'video') return;
    this._loadSlide(idx);

    const video = slide.querySelector('video');
    if (!video) return;
    if (!video.querySelector('source') && video.dataset.src) {
      const s = document.createElement('source');
      s.src = video.dataset.src;
      s.type = 'video/mp4';
      video.appendChild(s);
    }
    if (video.dataset.poster && video.poster.startsWith('data:')) video.poster = video.dataset.poster;

    video.muted = true;
    video.setAttribute('muted', '');
    this.videoPlaying = true;

    video.play().then(() => slide.classList.add('rv-playing')).catch(() => {
      this.videoPlaying = false;
    });

    video.onended = () => {
      slide.classList.remove('rv-playing');
      this.videoPlaying = false;
    };
  }

  _playCurrentVideo() {
    const slide = this.slides[this.index];
    if (!slide || slide.dataset.type !== 'video') return;
    this._loadSlide(this.index);

    const video = slide.querySelector('video');
    if (!video) return;
    if (!video.querySelector('source') && video.dataset.src) {
      const s = document.createElement('source');
      s.src = video.dataset.src;
      s.type = 'video/mp4';
      video.appendChild(s);
    }
    if (video.dataset.poster && video.poster.startsWith('data:')) video.poster = video.dataset.poster;

    this.videoPlaying = true;
    video.muted = false;
    video.removeAttribute('muted');
    video.play().then(() => slide.classList.add('rv-playing')).catch(() => {
      video.muted = true;
      video.setAttribute('muted', '');
      video.play().then(() => slide.classList.add('rv-playing')).catch(() => { this.videoPlaying = false; });
    });

    video.onended = () => {
      slide.classList.remove('rv-playing');
      this.videoPlaying = false;
    };
  }

  /* ── Touch / swipe ────────────────────────────────────────── */
  _onTouchStart(e) {
    if (e.touches.length > 1) return;
    this.touch.startX = e.touches[0].clientX;
    this.touch.startY = e.touches[0].clientY;
    this.touch.deltaX = 0;
    this.touch.time = Date.now();
    this.dragging = false;
    this.track.classList.add('rv-dragging');
  }

  _onTouchMove(e) {
    const t = e.touches[0];
    const dx = t.clientX - this.touch.startX;
    const dy = t.clientY - this.touch.startY;
    if (!this.dragging && Math.abs(dy) > Math.abs(dx)) { this.track.classList.remove('rv-dragging'); return; }
    if (!this.dragging && Math.abs(dx) < 8) return;
    if (!this.dragging) { this.dragging = true; e.preventDefault(); } else { e.preventDefault(); }
    this.touch.deltaX = dx;
    const pct = -this.index * 100 + (dx / this.viewer.offsetWidth) * 100;
    if (this.index === 0 && dx > 0) {
      this.track.style.transform = `translateX(${Math.min(pct, dx * 0.3 / this.viewer.offsetWidth * 100)}%)`;
    } else if (this.index === this.count - 1 && dx < 0) {
      this.track.style.transform = `translateX(${-this.index * 100 - Math.abs(dx) * 0.3 / this.viewer.offsetWidth * 100}%)`;
    } else {
      this.track.style.transform = `translateX(${pct}%)`;
    }
  }

  _onTouchEnd() {
    this.track.classList.remove('rv-dragging');
    if (!this.dragging) return;
    this.dragging = false;
    const dx = this.touch.deltaX;
    const dt = Date.now() - this.touch.time;
    const velocity = dt > 0 ? Math.abs(dx) / dt : 0;
    const threshold = this.viewer.offsetWidth * 0.2;
    this.track.style.transition = '';
    this.track.offsetHeight;
    if (Math.abs(dx) > threshold || velocity > 0.5) {
      dx < 0 ? this.next() : this.prev();
    } else {
      this.track.style.transform = `translateX(${-this.index * 100}%)`;
    }
  }
}


/* ────────────────────────────────────────────────────────────────
   ReviewsWall — outer reviews slider on mobile, wall on desktop.
   Uses CSS scroll-snap for native-feel sliding. JS adds arrows,
   dots, autoplay progress, and lazy-loads review media.
   ──────────────────────────────────────────────────────────────── */
class ReviewsWall {
  constructor(el) {
    this.el = el;
    this.wall = el.querySelector('.rv-wall');
    this.cards = [...el.querySelectorAll('.rv-card')];
    this.count = this.cards.length;
    this._galleries = [];

    if (this.count < 2) {
      this._initMediaGalleries();
      return;
    }

    this.index = 0;
    this.autoplayMs = Number(el.dataset.autoplay) || 0;
    this.autoplayTimer = null;
    this.autoplayPaused = false;
    this.scrolling = false;

    this.arrows = {
      prev: el.querySelector('.rv-arrow--prev'),
      next: el.querySelector('.rv-arrow--next'),
    };
    this.dots = [...el.querySelectorAll('.rv-dot')];
    this.progressBar = el.querySelector('.rv-progress');
    this.isMobile = false;

    this._onScrollEnd = this._onScrollEnd.bind(this);
    this._onProgressEnd = this._onProgressEnd.bind(this);

    this._init();
  }

  /* ── Init ─────────────────────────────────────────────────── */
  _init() {
    // Detect mobile via matchMedia
    this._mq = window.matchMedia('(max-width: 767px)');
    this._mq.onchange = () => this._onBreakpointChange();
    this.isMobile = this._mq.matches;

    // Initialize per-card media galleries
    this._initMediaGalleries();

    // Autoplay only while the reviews block is at least partially in view —
    // no timer, progress animation or sliding runs while it's off-screen.
    this._visObs = new IntersectionObserver(
      () => this._ensureAutoplayMatchesVisibility(),
      { threshold: 0 }
    );
    this._visObs.observe(this.el);

    if (!this.isMobile) return; // Desktop: CSS grid handles layout, no slider needed

    // Arrows
    if (this.arrows.prev) this.arrows.prev.addEventListener('click', () => this.prev());
    if (this.arrows.next) this.arrows.next.addEventListener('click', () => this.next());

    // Dots
    this.dots.forEach(d => d.addEventListener('click', () => this.goTo(Number(d.dataset.index))));

    // Detect which card is in view via scroll + IntersectionObserver
    this._trackActiveCard();

    // Pause autoplay on hover
    this.el.addEventListener('pointerenter', () => {
      if (this.autoplayMs > 0 && !this.autoplayPaused) this._pauseAutoplay();
    });
    this.el.addEventListener('pointerleave', () => {
      if (this.autoplayMs > 0 && !this.autoplayPaused) this._resumeAutoplay();
    });

    // Progress bar
    if (this.progressBar) {
      this.progressBar.addEventListener('animationend', this._onProgressEnd);
    }

    // Autoplay is started by the visibility observer (no start while off-screen)
  }

  _initMediaGalleries() {
    this.cards.forEach((card) => {
      const galleryEl = card.querySelector('.rv-gallery');
      if (galleryEl) {
        this._galleries.push(new ReviewMediaGallery(galleryEl));
      }
    });
  }

  /* ── Navigation ───────────────────────────────────────────── */
  goTo(idx) {
    if (idx < 0 || idx >= this.count) return;
    this.index = idx;
    const card = this.cards[idx];
    if (!card) return;

    this.scrolling = true;
    // Scroll only the wall's horizontal snap track. card.scrollIntoView() would
    // also scroll the window (html has scroll-behavior: smooth), so every
    // autoplay step animates the whole page down to the card.
    if (this.wall) {
      const left = card.getBoundingClientRect().left
                 - this.wall.getBoundingClientRect().left
                 + this.wall.scrollLeft;
      this.wall.scrollTo({ left, behavior: 'smooth' });
    }

    // Fallback: clear scrolling flag after animation
    clearTimeout(this._scrollTimeout);
    this._scrollTimeout = setTimeout(() => {
      this.scrolling = false;
      this._updateUI();
    }, 600);

    this._updateUI();
  }

  next() { this.goTo(this.index + 1); }
  prev() { this.goTo(this.index - 1); }

  play() {
    this.autoplayPaused = false;
    this._scheduleAutoplay();
  }

  pause() {
    this.autoplayPaused = true;
    this._clearAutoplay();
  }

  destroy() {
    this._clearAutoplay();
    this._galleries.forEach(g => g.destroy());
    if (this._mq) this._mq.onchange = null;
    if (this._cardObs) this._cardObs.disconnect();
    if (this._visObs) this._visObs.disconnect();
    if (this.arrows.prev) this.arrows.prev.removeEventListener('click', () => this.prev());
    if (this.arrows.next) this.arrows.next.removeEventListener('click', () => this.next());
    this.dots.forEach(d => d.removeEventListener('click', () => {}));
    if (this.progressBar) {
      this.progressBar.removeEventListener('animationend', this._onProgressEnd);
    }
  }

  /* ── UI update ────────────────────────────────────────────── */
  _updateUI() {
    this.dots.forEach((d, i) => {
      d.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
    });

    if (this.autoplayMs > 0 && !this.autoplayPaused) {
      this._restartProgress();
    }
  }

  /* ── Track active card via IntersectionObserver ───────────── */
  _trackActiveCard() {
    this._cardObs = new IntersectionObserver((entries) => {
      if (this.scrolling) return; // don't override programmatic scrolls
      // Find the entry with the highest intersection ratio
      let best = null;
      for (const e of entries) {
        if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
      }
      if (best && best.intersectionRatio > 0.5) {
        const idx = Number(best.target.dataset.index);
        if (idx !== this.index && !isNaN(idx)) {
          this.index = idx;
          this._updateUI();
        }
      }
    }, {
      root: this.wall,
      threshold: [0, 0.5, 1],
    });

    this.cards.forEach(c => this._cardObs.observe(c));
  }

  /* ── Autoplay ─────────────────────────────────────────────── */

  /**
   * Start/stop autoplay to match the current visibility of the block:
   * runs only when at least a part of it is inside the viewport.
   * Idempotent — safe to call from the observer and on breakpoint change.
   */
  _ensureAutoplayMatchesVisibility() {
    if (this.autoplayMs <= 0 || this.autoplayPaused || this.count < 2 || !this.isMobile) {
      this._clearAutoplay();
      return;
    }
    const r = this.el.getBoundingClientRect();
    const visible = r.bottom > 0 && r.top < window.innerHeight;
    if (visible) {
      this._scheduleAutoplay();
    } else {
      this._clearAutoplay();
    }
  }

  _scheduleAutoplay() {
    this._clearAutoplay();
    if (this.autoplayMs <= 0 || this.autoplayPaused || this.count < 2 || !this.isMobile) return;
    this.autoplayTimer = setTimeout(() => this.next(), this.autoplayMs);
    this._startProgress();
  }

  _clearAutoplay() {
    if (this.autoplayTimer) { clearTimeout(this.autoplayTimer); this.autoplayTimer = null; }
    this._stopProgress();
  }

  _pauseAutoplay() {
    if (!this.progressBar || !this.progressBar.classList.contains('rv-progress--running')) return;
    this.progressBar.style.animationPlayState = 'paused';
    if (this.autoplayTimer) { clearTimeout(this.autoplayTimer); this.autoplayTimer = null; }
  }

  _resumeAutoplay() {
    if (!this.progressBar || !this.progressBar.classList.contains('rv-progress--running')) return;
    this.progressBar.style.animationPlayState = 'running';
    const remaining = this.autoplayMs * 0.8;
    if (!this.autoplayTimer) {
      this.autoplayTimer = setTimeout(() => this.next(), Math.max(remaining, 500));
    }
  }

  _startProgress() {
    if (!this.progressBar) return;
    this.progressBar.classList.remove('rv-progress--running');
    this.progressBar.style.setProperty('--rv-autoplay-ms', `${this.autoplayMs}ms`);
    void this.progressBar.offsetWidth;
    this.progressBar.classList.add('rv-progress--running');
  }

  _stopProgress() {
    if (!this.progressBar) return;
    this.progressBar.classList.remove('rv-progress--running');
  }

  _restartProgress() {
    this._clearAutoplay();
    if (this.autoplayMs <= 0 || this.autoplayPaused || this.count < 2 || !this.isMobile) return;
    this.autoplayTimer = setTimeout(() => this.next(), this.autoplayMs);
    this._startProgress();
  }

  _onProgressEnd() { this.next(); }

  _onScrollEnd() {
    this.scrolling = false;
    this._updateUI();
    if (this.autoplayMs > 0 && !this.autoplayPaused) this._scheduleAutoplay();
  }

  _onBreakpointChange() {
    const wasMobile = this.isMobile;
    this.isMobile = this._mq.matches;
    if (this.isMobile && !wasMobile) {
      // Switched to mobile: set up slider
      this._trackActiveCard();
      this._ensureAutoplayMatchesVisibility();
    } else if (!this.isMobile && wasMobile) {
      // Switched to desktop: tear down slider
      this._clearAutoplay();
      if (this._cardObs) this._cardObs.disconnect();
    }
  }
}


/* ── Auto-initialize ───────────────────────────────────────────── */
if (typeof document !== 'undefined') {
  const boot = () => {
    document.querySelectorAll('.rv-section').forEach(el => new ReviewsWall(el));
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}

if (typeof window !== 'undefined') {
  window.ReviewsWall = ReviewsWall;
  window.ReviewMediaGallery = ReviewMediaGallery;
}